import AdminModalShell from "./admin/AdminModalShell";
import { Plus, Trash2 } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTheme } from "../contexts/ThemeContext";
import userContractExclusionsService, { UserContractExclusion } from "../services/userContractExclusionsService";
import ConfirmationModal from "./ConfirmationModal";

export interface ContractExclusionServiceFunctions {
  getExclusions: (userId: string) => Promise<{ exclusions: UserContractExclusion[] }>;
  createExclusion: (params: { userId: string; conId: number; message?: string }) => Promise<UserContractExclusion>;
  deleteExclusion: (userId: string, conId: number) => Promise<void>;
  deleteAllExclusions?: (userId: string) => Promise<number>;
}

interface ContractExclusionsModalProps {
  appearance?: "default" | "alpha";
  isOpen: boolean;
  onClose: () => void;
  userId: string | null;
  userName?: string | null;
  serviceFunctions?: ContractExclusionServiceFunctions;
}

const ContractExclusionsModal: React.FC<ContractExclusionsModalProps> = ({
  appearance = "default", isOpen, onClose, userId, userName, serviceFunctions }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const owner = isOpen ? userId : null;
  const ownerRef = useRef(owner);
  ownerRef.current = owner;
  const requestRef = useRef(0);
  const mutationRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exclusions, setExclusions] = useState<UserContractExclusion[]>([]);
  
  // Form state for adding new exclusion
  const [newConId, setNewConId] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [addingExclusion, setAddingExclusion] = useState(false);

  // Confirmation modal state
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<"delete" | "delete-all" | null>(null);
  const [selectedExclusion, setSelectedExclusion] = useState<UserContractExclusion | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const resetForm = () => {
    setNewConId("");
    setNewMessage("");
    setExclusions([]);
    setError(null);
    setAddingExclusion(false);
  };

  const fetchExclusions = useCallback(async (id: string) => {
    const requestId = ++requestRef.current;
    const isCurrent = () => ownerRef.current === id && requestRef.current === requestId;
    try {
      setLoading(true);
      setError(null);
      const data = serviceFunctions
        ? await serviceFunctions.getExclusions(id)
        : await userContractExclusionsService.getExclusionsForUser(id);
      if (isCurrent()) setExclusions(data.exclusions || []);
    } catch (err: any) {
      if (!isCurrent()) return;
      console.error("Failed to fetch contract exclusions:", err);
      const status = err.response?.status || err.status;
      if (status === 404 || status === 400) {
        setExclusions([]);
      } else {
        setError(err.response?.data?.error || err.message || "Failed to fetch exclusions");
      }
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [serviceFunctions]);

  useEffect(() => {
    const invalidate = () => { ++requestRef.current; ++mutationRef.current; };
    resetForm();
    setConfirmModalOpen(false);
    setConfirmLoading(false);
    setConfirmAction(null);
    setSelectedExclusion(null);
    if (isOpen && userId) void fetchExclusions(userId);
    return invalidate;
  }, [isOpen, userId, fetchExclusions]);

  const handleAddExclusion = async () => {
    if (!userId || !newConId.trim()) {
      setError("Contract ID is required");
      return;
    }

    const conIdNum = parseInt(newConId.trim(), 10);
    if (isNaN(conIdNum)) {
      setError("Contract ID must be a valid number");
      return;
    }

    const mutationId = ++mutationRef.current;
    const targetUserId = userId;
    const isCurrent = () => ownerRef.current === targetUserId && mutationRef.current === mutationId;
    try {
      setAddingExclusion(true);
      setError(null);
      
      if (serviceFunctions) {
        await serviceFunctions.createExclusion({
          userId,
          conId: conIdNum,
          message: newMessage.trim() || undefined,
        });
      } else {
        await userContractExclusionsService.createExclusion({
          userId,
          conId: conIdNum,
          message: newMessage.trim() || undefined,
        });
      }

      if (!isCurrent()) return;
      // Reset form and refresh list
      setNewConId("");
      setNewMessage("");
      await fetchExclusions(userId);
    } catch (err: any) {
      if (!isCurrent()) return;
      console.error("Failed to create contract exclusion:", err);
      const status = err.response?.status || err.status;
      if (status === 409) {
        setError("This contract exclusion already exists for this user");
      } else {
        setError(err.response?.data?.error || err.message || "Failed to create exclusion");
      }
    } finally {
      if (isCurrent()) setAddingExclusion(false);
    }
  };

  const handleDeleteExclusion = async () => {
    if (!userId || !selectedExclusion) return;

    const mutationId = ++mutationRef.current;
    const targetUserId = userId;
    const isCurrent = () => ownerRef.current === targetUserId && mutationRef.current === mutationId;
    try {
      setConfirmLoading(true);
      if (serviceFunctions) {
        await serviceFunctions.deleteExclusion(userId, selectedExclusion.conId);
      } else {
        await userContractExclusionsService.deleteExclusion(userId, selectedExclusion.conId);
      }
      if (!isCurrent()) return;
      setConfirmModalOpen(false);
      setSelectedExclusion(null);
      setConfirmAction(null);
      await fetchExclusions(userId);
    } catch (err: any) {
      if (!isCurrent()) return;
      console.error("Failed to delete contract exclusion:", err);
      setError(err.response?.data?.error || err.message || "Failed to delete exclusion");
      setConfirmModalOpen(false);
    } finally {
      if (isCurrent()) setConfirmLoading(false);
    }
  };

  const handleDeleteAllExclusions = async () => {
    if (!userId) return;

    const mutationId = ++mutationRef.current;
    const targetUserId = userId;
    const isCurrent = () => ownerRef.current === targetUserId && mutationRef.current === mutationId;
    try {
      setConfirmLoading(true);
      const deleteAllFn = serviceFunctions?.deleteAllExclusions ?? userContractExclusionsService.deleteAllExclusions;
      const deletedCount = await deleteAllFn(userId);
      if (!isCurrent()) return;
      console.log(`Deleted ${deletedCount} exclusions`);
      setConfirmModalOpen(false);
      setConfirmAction(null);
      await fetchExclusions(userId);
    } catch (err: any) {
      if (!isCurrent()) return;
      console.error("Failed to delete all contract exclusions:", err);
      setError(err.response?.data?.error || err.message || "Failed to delete all exclusions");
      setConfirmModalOpen(false);
    } finally {
      if (isCurrent()) setConfirmLoading(false);
    }
  };

  const openDeleteConfirmation = (exclusion: UserContractExclusion) => {
    setSelectedExclusion(exclusion);
    setConfirmAction("delete");
    setConfirmModalOpen(true);
  };

  const openDeleteAllConfirmation = () => {
    setConfirmAction("delete-all");
    setConfirmModalOpen(true);
  };

  const handleConfirm = () => {
    if (confirmAction === "delete") {
      handleDeleteExclusion();
    } else if (confirmAction === "delete-all") {
      handleDeleteAllExclusions();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <AdminModalShell appearance={appearance} isOpen={isOpen} onClose={onClose}
        title={`Contract Exclusions for ${userName || userId}`} maxWidthClassName="max-w-4xl"
        closeDisabled={addingExclusion || confirmLoading || confirmModalOpen}
        footer={
            <div className={appearance === "alpha"? `flex items-center justify-end gap-3 border-v7-ink/15` : `flex items-center justify-end gap-3 ${isDarkMode ?"border-gray-700" : "border-gray-200"}`}>
            <button
              type="button"
              onClick={onClose}
              disabled={addingExclusion || confirmLoading}
              className={appearance === "alpha" ? `px-4 py-2 rounded-v7-panel transition-colors text-v7-ink-soft hover:text-v7-ink-soft hover:bg-v7-paper` : `px-4 py-2 rounded-lg transition-colors ${
                isDarkMode
                  ? "text-gray-300 hover:text-white hover:bg-gray-700"
                  : "text-gray-600 hover:text-gray-700 hover:bg-gray-50"
              }`}
            >
              Close
            </button>
          </div>
        }>
          {/* Content */}
          <div className="space-y-6">
            {/* Error Display */}
            {error && (
              <div className={appearance === "alpha"? `p-4 rounded-v7-control bg-v7-rust/5 text-v7-rust` : `p-4 rounded-md ${isDarkMode ?"bg-red-900/30 text-red-300" : "bg-red-50 text-red-700"}`}>
                {error}
              </div>
            )}

            {/* Add New Exclusion Form */}
            <div className={appearance === "alpha"? `p-4 rounded-v7-panel border border-v7-ink/15 bg-v7-paper` : `p-4 rounded-lg border ${isDarkMode ?"border-gray-700 bg-gray-750" : "border-gray-200 bg-gray-50"}`}>
              <h3 className={appearance === "alpha"? `text-lg font-medium mb-4 text-v7-ink` : `text-lg font-medium mb-4 ${isDarkMode ?"text-white" : "text-gray-900"}`}>
                Add New Exclusion
              </h3>
              <div className="space-y-4">
                <div>
                  <label className={appearance === "alpha"? `block text-sm font-medium mb-1 text-v7-ink-soft` : `block text-sm font-medium mb-1 ${isDarkMode ?"text-gray-300" : "text-gray-700"}`}>
                    Contract ID <span className={appearance === "alpha" ? "text-v7-rust" : "text-red-500"}>*</span>
                  </label>
                  <input
                    aria-label="Contract ID"
                    type="number"
                    value={newConId}
                    onChange={(e) => setNewConId(e.target.value)}
                    placeholder="e.g. 123456"
                    className={appearance === "alpha" ? `w-full px-3 py-2 border rounded-v7-panel focus:ring-2 focus:ring-v7-bronze-dark focus:border-transparent border-v7-ink/25` : `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      isDarkMode ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400" : "border-gray-300"
                    }`}
                    disabled={addingExclusion}
                  />
                </div>
                <div>
                  <label className={appearance === "alpha"? `block text-sm font-medium mb-1 text-v7-ink-soft` : `block text-sm font-medium mb-1 ${isDarkMode ?"text-gray-300" : "text-gray-700"}`}>
                    Message (optional)
                  </label>
                  <textarea aria-label="Exclusion message"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Optional explanation for this exclusion"
                    rows={2}
                    className={appearance === "alpha" ? `w-full px-3 py-2 border rounded-v7-panel focus:ring-2 focus:ring-v7-bronze-dark focus:border-transparent border-v7-ink/25` : `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      isDarkMode ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400" : "border-gray-300"
                    }`}
                    disabled={addingExclusion}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleAddExclusion}
                  disabled={addingExclusion || !newConId.trim()}
                  className={appearance === "alpha" ? "px-4 py-2 bg-v7-ink text-v7-white rounded-v7-panel hover:bg-v7-ink transition-colors disabled:opacity-50 flex items-center" : "px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center"}
                >
                  {addingExclusion ? (
                    <div className={appearance === "alpha" ? "animate-spin rounded-full h-4 w-4 border-b-2 border-v7-white mr-2" : "animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"}></div>
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  {addingExclusion ? "Adding..." : "Add Exclusion"}
                </button>
              </div>
            </div>

            {/* Exclusions List */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className={appearance === "alpha"? `text-lg font-medium text-v7-ink` : `text-lg font-medium ${isDarkMode ?"text-white" : "text-gray-900"}`}>
                  Existing Exclusions ({exclusions.length})
                </h3>
                {exclusions.length > 0 && (!serviceFunctions || serviceFunctions.deleteAllExclusions) && (
                  <button
                    type="button"
                    onClick={openDeleteAllConfirmation}
                    className={appearance === "alpha" ? `text-sm px-3 py-1 rounded-v7-panel transition-colors text-v7-rust hover:text-v7-rust hover:bg-v7-rust/5` : `text-sm px-3 py-1 rounded-lg transition-colors ${
                      isDarkMode
                        ? "text-red-400 hover:text-red-300 hover:bg-red-900/20"
                        : "text-red-600 hover:text-red-700 hover:bg-red-50"
                    }`}
                  >
                    Delete All
                  </button>
                )}
              </div>

              {loading && exclusions.length === 0 ? (
                <div className="flex justify-center py-8">
                  <div className={appearance === "alpha" ? "animate-spin rounded-full h-8 w-8 border-b-2 border-v7-ink/25" : "animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"}></div>
                </div>
              ) : exclusions.length === 0 ? (
                <p className={appearance === "alpha"? `text-sm italic text-center py-8 text-v7-ink/60` : `text-sm italic text-center py-8 ${isDarkMode ?"text-gray-500" : "text-gray-400"}`}>
                  No contract exclusions found for this user.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className={appearance === "alpha"? `min-w-full divide-y divide-v7-ink/15` : `min-w-full divide-y ${isDarkMode ?"divide-gray-700" : "divide-gray-200"}`}>
                    <thead className={appearance === "alpha" ? "bg-v7-paper" : isDarkMode ? "bg-gray-700" : "bg-gray-50"}>
                      <tr>
                        <th className={appearance === "alpha"? `px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-v7-ink/60` : `px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ?"text-gray-300" : "text-gray-500"}`}>
                          Contract ID
                        </th>
                        <th className={appearance === "alpha"? `px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-v7-ink/60` : `px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ?"text-gray-300" : "text-gray-500"}`}>
                          Message
                        </th>
                        <th className={appearance === "alpha"? `px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-v7-ink/60` : `px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ?"text-gray-300" : "text-gray-500"}`}>
                          Created
                        </th>
                        <th className={appearance === "alpha"? `px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-v7-ink/60` : `px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ?"text-gray-300" : "text-gray-500"}`}>
                          Created By
                        </th>
                        <th className={appearance === "alpha"? `px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-v7-ink/60` : `px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ?"text-gray-300" : "text-gray-500"}`}>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className={appearance === "alpha"? `bg-v7-white divide-v7-ink/15 divide-y` : `${isDarkMode ?"bg-gray-800 divide-gray-700" : "bg-white divide-gray-200"} divide-y`}>
                      {exclusions.map((exclusion) => (
                        <tr key={exclusion.id} className={appearance === "alpha" ? "hover:bg-v7-paper" : isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-50"}>
                          <td className={appearance === "alpha"? `px-4 py-3 whitespace-nowrap text-sm font-mono text-v7-ink` : `px-4 py-3 whitespace-nowrap text-sm font-mono ${isDarkMode ?"text-white" : "text-gray-900"}`}>
                            {exclusion.conId}
                          </td>
                          <td className={appearance === "alpha"? `px-4 py-3 text-sm text-v7-ink-soft` : `px-4 py-3 text-sm ${isDarkMode ?"text-gray-300" : "text-gray-600"}`}>
                            {exclusion.message || <span className={appearance === "alpha" ? "italic text-v7-ink/60" : "italic text-gray-500"}>No message</span>}
                          </td>
                          <td className={appearance === "alpha"? `px-4 py-3 whitespace-nowrap text-sm text-v7-ink/60` : `px-4 py-3 whitespace-nowrap text-sm ${isDarkMode ?"text-gray-400" : "text-gray-500"}`}>
                            {new Date(exclusion.createdAtUtc).toLocaleString()}
                          </td>
                          <td className={appearance === "alpha"? `px-4 py-3 whitespace-nowrap text-sm font-mono text-v7-ink/60` : `px-4 py-3 whitespace-nowrap text-sm font-mono ${isDarkMode ?"text-gray-400" : "text-gray-500"}`}>
                            {exclusion.createdBy.substring(0, 8)}...
                          </td>
                          <td className={appearance === "alpha"? `px-4 py-3 whitespace-nowrap text-sm text-v7-ink/60` : `px-4 py-3 whitespace-nowrap text-sm ${isDarkMode ?"text-gray-400" : "text-gray-500"}`}>
                            <button
                              onClick={() => openDeleteConfirmation(exclusion)}
                              className={appearance === "alpha" ? `p-1 rounded-full hover:bg-v7-paper text-v7-rust` : `p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 ${
                                isDarkMode ? "text-red-400" : "text-red-600"
                              }`}
                              title="Delete exclusion"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

      </AdminModalShell>

      {/* Confirmation Modal */}
      <ConfirmationModal appearance={appearance}
        isOpen={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setConfirmAction(null);
          setSelectedExclusion(null);
        }}
        onConfirm={handleConfirm}
        title={confirmAction === "delete-all" ? "Delete All Exclusions" : "Delete Exclusion"}
        message={
          confirmAction === "delete-all"
            ? `Are you sure you want to delete all ${exclusions.length} contract exclusions for this user? This action cannot be undone.`
            : `Are you sure you want to delete the exclusion for contract ID ${selectedExclusion?.conId}? This action cannot be undone.`
        }
        confirmText="Delete"
        cancelText="Cancel"
        isDestructive={true}
        loading={confirmLoading}
      />
    </>
  );
};

export default ContractExclusionsModal;
